import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, Phone } from "lucide-react";
import { mediaUrl } from "../lib/api";
import { enquireHref, pageHref } from "../lib/format";
import Breadcrumbs from "../components/Breadcrumbs";
import SmartImage from "../components/SmartImage";

/* ------------------------------------------------------------------ *
 * Shared primitives for the page templates.
 * Each template composes these differently so the 116 migrated pages
 * no longer all render through one identical layout.
 * ------------------------------------------------------------------ */

export function useEnquiry(page, heroImg, parentLabel) {
  return enquireHref({
    type: "page",
    id: page.slug,
    title: page.title,
    image: heroImg,
    category: parentLabel,
  });
}

export function SectionHead({ eyebrow, title, sub, right, align = "left" }) {
  return (
    <div
      className={`mb-7 flex flex-wrap items-end gap-4 ${
        align === "center" ? "flex-col items-center text-center" : "justify-between"
      }`}
    >
      <div className={align === "center" ? "max-w-2xl" : "max-w-2xl"}>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h2 className="mt-2 text-2xl font-bold leading-tight text-[color:var(--nq-sand)] md:text-3xl">
          {title}
        </h2>
        {sub && <p className="mt-3 text-sm leading-relaxed text-[color:var(--nq-stone)]">{sub}</p>}
        <div className="gold-rule mt-4 w-24" />
      </div>
      {right}
    </div>
  );
}

/** Small brand mark plate - logos need a light backing to stay legible. */
export function BrandPlate({ src, alt, className = "" }) {
  if (!src) return null;
  return (
    <div
      className={`inline-flex items-center justify-center rounded-lg px-4 py-3 ${className}`}
      style={{ background: "rgba(233,226,210,0.94)", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}
      data-testid="brand-plate"
    >
      <img src={mediaUrl(src, 320)} alt={alt || "Brand"} className="h-9 w-auto max-w-[170px] object-contain md:h-11" />
    </div>
  );
}

export function CtaPair({ to, label = "Enquire now", phone, testid = "page-enquire-button", compact }) {
  return (
    <div className="flex flex-wrap gap-3">
      <Link to={to} className={`btn-gold ${compact ? "px-4 py-2 text-xs" : ""}`} data-testid={testid}>
        <MessageSquare className="h-4 w-4" /> {label}
      </Link>
      {phone && (
        <a
          href={`tel:${phone.replace(/[^\d+]/g, "")}`}
          className={`btn-outline-nq ${compact ? "px-4 py-2 text-xs" : ""}`}
          data-testid="page-call-button"
        >
          <Phone className="h-4 w-4" /> {phone}
        </a>
      )}
    </div>
  );
}

/** Compact breadcrumb + eyebrow header used above every template hero. */
export function CrumbLine({ breadcrumbs, parentLabel }) {
  return (
    <>
      <Breadcrumbs items={breadcrumbs} />
      {parentLabel && <p className="eyebrow mt-4">{parentLabel}</p>}
    </>
  );
}

/** Sticky "talk to us" card used by the prose-led templates. */
export function EnquiryRail({ page, to, phone, siblings = [], noun }) {
  return (
    <div className="sticky top-28 space-y-4">
      <div className="card-nq p-5">
        <p className="eyebrow">Talk to the team</p>
        <h3 className="mt-2 text-lg font-semibold text-[color:var(--nq-sand)]">Need help choosing?</h3>
        <p className="mt-2 text-sm leading-relaxed text-[color:var(--nq-stone)]">
          Tell us your vehicle and what you tow or carry, and we&apos;ll match the right{" "}
          {(noun || page.title).toLowerCase()} setup.
        </p>
        <div className="mt-4 flex flex-col gap-2">
          <Link to={to} className="btn-gold w-full" data-testid="sidebar-enquire-button">
            <MessageSquare className="h-4 w-4" /> Enquire now
          </Link>
          {phone && (
            <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className="btn-outline-nq w-full">
              <Phone className="h-4 w-4" /> {phone}
            </a>
          )}
        </div>
      </div>

      {siblings.length > 0 && (
        <div className="card-nq p-5" data-testid="related-rail">
          <p className="eyebrow">Related categories</p>
          <ul className="mt-3 space-y-1">
            {siblings.slice(0, 10).map((s) => (
              <li key={s.slug}>
                <Link
                  to={pageHref(s.slug)}
                  className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-[color:var(--nq-stone)] transition-colors duration-200 hover:bg-white/5 hover:text-[color:var(--nq-gold)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--nq-gold)]"
                  data-testid="related-rail-link"
                >
                  {s.label}
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Horizontal pill nav of sibling pages - keeps deep pages navigable. */
export function SiblingPills({ siblings = [], title = "Also in this section" }) {
  if (!siblings.length) return null;
  return (
    <section data-testid="sibling-pills">
      <p className="eyebrow">{title}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {siblings.map((s) => (
          <Link
            key={s.slug}
            to={pageHref(s.slug)}
            className="rounded-full border px-3.5 py-1.5 text-xs font-medium text-[color:var(--nq-stone)] transition-colors duration-200 hover:border-[color:var(--nq-gold)] hover:text-[color:var(--nq-gold)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--nq-gold)]"
            style={{ borderColor: "rgba(255,255,255,0.12)" }}
            data-testid="sibling-pill"
          >
            {s.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

/** Framed showcase photo with gold corner rules - used in split heroes. */
export function FramedShot({ src, alt, ratio = "aspect-[4/3]", fit = "cover" }) {
  return (
    <div className="relative" data-testid="framed-shot">
      <div
        className="pointer-events-none absolute -left-3 -top-3 h-16 w-16 rounded-tl-xl border-l-2 border-t-2"
        style={{ borderColor: "rgba(198,162,74,0.55)" }}
      />
      <div
        className="pointer-events-none absolute -bottom-3 -right-3 h-16 w-16 rounded-br-xl border-b-2 border-r-2"
        style={{ borderColor: "rgba(198,162,74,0.55)" }}
      />
      <div className="card-nq overflow-hidden">
        <SmartImage src={src} alt={alt} ratio={ratio} fit={fit} loading="eager" w={1200} />
      </div>
    </div>
  );
}

/** Full-width dark band with the page photo washed behind it. */
export function HeroBand({ image, children, tall = false, dim = 0.22 }) {
  return (
    <section
      className="relative overflow-hidden border-b"
      style={{ borderColor: "rgba(255,255,255,0.08)" }}
      data-testid="page-hero"
    >
      <div className="absolute inset-0">
        {image && (
          <img
            src={mediaUrl(image, 1920)}
            alt=""
            className="h-full w-full object-cover"
            style={{ opacity: dim }}
          />
        )}
        <div className="absolute inset-0 hero-overlay" />
        <div className="noise-overlay" />
      </div>
      <div className={`container-nq relative ${tall ? "py-14 md:py-20" : "py-10 md:py-14"}`}>
        {children}
      </div>
    </section>
  );
}

export function StatChip({ label, value }) {
  return (
    <div
      className="rounded-lg border px-3.5 py-2"
      style={{ borderColor: "rgba(198,162,74,0.28)", background: "rgba(198,162,74,0.06)" }}
      data-testid="stat-chip"
    >
      <p className="font-mono text-base font-semibold gold-text">{value}</p>
      <p className="mt-0.5 text-[11px] uppercase tracking-wider text-[color:var(--nq-stone)]">{label}</p>
    </div>
  );
}

export function EmptyCta({ page, to, siblings = [] }) {
  return (
    <section className="card-nq p-8 md:p-12" data-testid="empty-page-cta">
      <div className="mx-auto max-w-xl text-center">
        <h2 className="text-xl font-semibold text-[color:var(--nq-sand)] md:text-2xl">
          {page.title}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-[color:var(--nq-stone)]">
          We supply and fit this range. Get in touch and we&apos;ll confirm options, pricing and
          fitment for your vehicle.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to={to} className="btn-gold" data-testid="empty-page-enquire-button">
            <MessageSquare className="h-4 w-4" /> Enquire about {page.title}
          </Link>
        </div>
      </div>
      {siblings.length > 0 && (
        <div className="mt-8 border-t pt-6" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <SiblingPills siblings={siblings} />
        </div>
      )}
    </section>
  );
}
